import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import CartButton from "./CartButton";
import { jwtDecode } from "jwt-decode";

const CATEGORY_OPTIONS = [
  { en: "All Products", fr: "Tous les produits", field: "category" },
  { en: "Kits", fr: "Kits", field: "type" },
  { en: "Bars", fr: "Barres", field: "category" },
  { en: "Breakfast", fr: "Déjeuner", field: "category" },
  { en: "Condiments and Specialties", fr: "Condiments et spécialités", field: "category" },
  { en: "Drinks", fr: "Boissons", field: "category" },
  { en: "Entrees and Sides", fr: "Plats principaux et accompagnements", field: "category" },
  { en: "Restricted", fr: "Restreint", field: "restricted"},
  { en: "Salty Snacks", fr: "Collations salées", field: "category" },
  { en: "Soups", fr: "Soupes", field: "category" },
  { en: "Supplements", fr: "Suppléments", field: "category" },
  { en: "Sweet Snacks", fr: "Collations sucrées", field: "category" },
];

const DIETARY_OPTIONS = [
  { en: "Dairy Free", fr: "Sans produits laitiers" },
  { en: "Egg Free", fr: "Sans œufs" },
  { en: "Gluten Free", fr: "Sans gluten" },
  { en: "Milk Free", fr: "Sans lait" },
  { en: "Peanut Free", fr: "Sans arachides" },
  { en: "Soy Free", fr: "Sans soja" },
  { en: "Treenut Free", fr: "Sans noix" },
  { en: "Vegetarian", fr: "Végétarien" },
  { en: "Kosher", fr: "Kasher" },
  { en: "Sweetened with Stevia", fr: "Sucré avec stévia" },
  { en: "Restricted", fr: "Restreint" },
  { en: "Non Restricted", fr: "Non restreint" },
];

const Shop = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState({ value: "All Products", field: "category" });
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [showDietaryFilters, setShowDietaryFilters] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { i18n, t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const language = i18n.language;

  const handleClickOutside = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setShowPopup(false);
    }
  };

  useEffect(() => {
    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPopup]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded?.role === "admin") {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error("Invalid token", error);
      }
    }
  }, []);

  const handleClick = () => {
    sessionStorage.setItem("shopScrollY", window.scrollY);
  };

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

useEffect(() => {
  fetch(`${API_URL}products`)
    .then((res) => res.json())
    .then((data) => {
      // Sort the products immediately based on language before setting state
      const sortedData = [...data].sort((a, b) => {
        const nameA = (a.name[language] || a.name.en).toLowerCase();
        const nameB = (b.name[language] || b.name.en).toLowerCase();

        const isPriorityA = nameA.includes("phase") || nameA.includes("protocol");
        const isPriorityB = nameB.includes("phase") || nameB.includes("protocol");

        const extractNumber = (str) => {
          const match = str.match(/(?:phase|protocol)\s*(\d+)/i);
          return match ? parseInt(match[1], 10) : null;
        };

        if (isPriorityA && !isPriorityB) return -1;
        if (!isPriorityA && isPriorityB) return 1;

        if (isPriorityA && isPriorityB) {
          const numA = extractNumber(nameA);
          const numB = extractNumber(nameB);
          if (numA !== null && numB !== null) return numA - numB;
          if (numA !== null) return -1;
          if (numB !== null) return 1;
        }

        return nameA.localeCompare(nameB);
      });

      setProducts(sortedData);
      setFilteredProducts(sortedData);
      setLoading(false);
      
      const savedScrollY = sessionStorage.getItem("shopScrollY");
      if (savedScrollY) {
        window.scrollTo(0, parseInt(savedScrollY));
        sessionStorage.removeItem("shopScrollY");
      }
    })
    .catch((error) => {
      console.error("Error fetching products:", error);
      setLoading(false);
    });
}, [API_URL, language]); 

  useEffect(() => {
  let temp = [...products];

  let selectedFilterEN = selectedFilter.value;
  if (language === "fr") {
    const match = CATEGORY_OPTIONS.find((opt) => opt.fr === selectedFilter.value);
    if (match) selectedFilterEN = match.en;
  }

  if (selectedFilterEN !== "All Products") {
    const field = selectedFilter.field;
    if (field === "category") {
      temp = temp.filter((product) => product.category?.en === selectedFilterEN);
    } else if (field === "type") {
      temp = temp.filter((product) => product.type === "kit");
    } else if (field === "restricted") {
      temp = temp.filter((product) => product.restricted?.en === "Yes");
    }
  }

  const selectedDietaryEN = selectedDietary.map((label) =>
    DIETARY_OPTIONS.find(opt => opt[language] === label)?.en || label
  );

  if (selectedDietaryEN.length > 0) {
    temp = temp.filter((product) =>
      selectedDietaryEN.every((restriction) => {
        const dietary = product.dietaryRestriction?.en || [];
        const allergens = product.allergens?.en || [];
        const restricted = product.restricted?.en;

        if (restriction === "Restricted") return restricted === "Yes";
        if (restriction === "Non Restricted") return restricted === "No";
        
        const allergyMap = {
          "Dairy Free": "Milk",
          "Egg Free": "Egg",
          "Milk Free": "Milk",
          "Peanut Free": "Peanuts",
          "Soy Free": "Soy",
          "Treenut Free": "Almonds",
        };

        if (allergyMap[restriction]) {
          return !allergens.includes(allergyMap[restriction]);
        }

        return dietary.includes(restriction);
      })
    );
  }

  if (searchQuery) {
    temp = temp.filter((product) => {
      const productName = product.name[language] || product.name.en;
      const productDescription = product.description[language] || product.description.en;
      return (
        productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        productDescription.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }

  // ---- MOVE "Mrs Taste" PRODUCTS TO THE END ----
  temp.sort((a, b) => {
    const aIsMrsTaste = a.name[language]?.toLowerCase().includes("mrs taste");
    const bIsMrsTaste = b.name[language]?.toLowerCase().includes("mrs taste");

    if (aIsMrsTaste && !bIsMrsTaste) return 1;  // a goes after b
    if (!aIsMrsTaste && bIsMrsTaste) return -1; // b goes after a
    return 0; // leave other order unchanged
  });

  setFilteredProducts(temp);
}, [products, selectedFilter, selectedDietary, language, searchQuery]);

  const toggleDietary = (item) => {
    setSelectedDietary((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

const resetFilters = () => {
  setSelectedFilter({ value: "All Products", field: "category" });
  setSelectedDietary([]);
  setSearchQuery("");
};

  const updateAvailability = async (productId, newStatus) => {
    const availability = {
      en: newStatus,
      fr: newStatus === "Out of Stock" ? "Rupture de stock" :
          newStatus === "Discontinued" ? "Discontinué" :
          "Disponible"
    };
  
    try {
      const res = await fetch(`${API_URL}${productId}/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability })
      });
  
      if (!res.ok) throw new Error('Update failed');
  
      setProducts(prev => prev.map(p => 
        p._id === productId ? { ...p, availability } : p
      ));
    } catch (err) {
      console.error("Availability update failed", err);
      alert("Could not update availability of product.");
    }
  };
  
  return (
    <div>
      <Header />
      <ShopBody>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>{t("loading.products")}</LoadingText>
          </LoadingContainer>
        ) : (
          <>
    <QcDisclaimerWrapper>
      <FullDisclaimer>{t("disclaimer.purchaseNotice")}</FullDisclaimer>
      <DisclaimerButton onClick={() => setShowPopup(!showPopup)}>?</DisclaimerButton>
      {showPopup && <PopupDisclaimer ref={popupRef}>{t("disclaimer.purchaseNotice")}</PopupDisclaimer>}
    </QcDisclaimerWrapper>
        <Toppagediv>
        <Title>{t("shop.title")}</Title>
        <QcDisclaimer>{t("disclaimer.quebec")}</QcDisclaimer>
        </Toppagediv>
        <FilterWrapper>
          <FilterGroup>
  <ButtonGrid>
    {CATEGORY_OPTIONS.map((option) => {
  const isSelected = selectedFilter.value === option.en;
  return (
    <CategoryButton
      key={option.en}
      onClick={() => setSelectedFilter({ value: option.en, field: option.field })}
      selected={isSelected}
    >
      {option[language]}
    </CategoryButton>
  );
})}
  </ButtonGrid>
</FilterGroup>
          <BtnSearchBar>
            <SearchInput type="text" placeholder={t('Search for a product')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <CartButton />
          </BtnSearchBar>
          
          <FilterGroup>
            <DietaryHeader>
              <ClickableHeaderContent onClick={() => setShowDietaryFilters(prev => !prev)}>
                {language === "fr" ? "Restrictions alimentaires" : "Dietary Restrictions"}
                <Arrow open={showDietaryFilters}>▼</Arrow>
              </ClickableHeaderContent>
            </DietaryHeader>
            <DietaryFilterContainer open={showDietaryFilters}>
              <CheckboxGrid>
                {DIETARY_OPTIONS.map((option) => {
                  const isChecked = selectedDietary.includes(option.en);
                  return (
                    <CheckboxWrapper key={option.en}>
                      <HiddenCheckbox type="checkbox" checked={isChecked} onChange={() => toggleDietary(option.en)} />
                      <StyledCheckbox checked={isChecked}>
                        <Checkmark viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" />
                        </Checkmark>
                      </StyledCheckbox>
                      {option[language]}
                    </CheckboxWrapper>
                  );
                })}
              </CheckboxGrid>
            </DietaryFilterContainer>
          </FilterGroup>
          <ResetButton onClick={resetFilters}>
            {language === "fr" ? "Réinitialiser les filtres" : "Reset Filters"}
          </ResetButton>
        </FilterWrapper>
        <GridContainer>
          {filteredProducts.map((product) => {
            const productName = product.name[language] || product.name.en;
            const productDescription = product.description[language] || product.description.en;
            return (
              <StyledLink key={product._id} to={`/shop/${product._id}`} onClick={handleClick}>
                <ProductCard>
                  <ImageContainer>
                    {product.restricted?.en === "Yes" && (
                      <RestrictedIcon src="https://ip-products.idealprotein.com/assets/images/product-details/Icon-Restriced.svg" alt="Restricted Icon"/>
                    )}
                    {(product.availability?.en === "Out of Stock" || product.availability?.en === "Discontinued") && (
                      <AvailabilityBubble type={product.availability?.en === "Discontinued" ? "Discontinued" : "Out of Stock"}>
                        {product.availability?.[i18n.language]}
                      </AvailabilityBubble>
                    )}
                    <Image src={product.images[0]} alt={product.name[i18n.language]} />
                  </ImageContainer>
                  <ProductTitle>{productName}</ProductTitle>
                  {product.type === "kit" && product.slogan && (
                    <ProductSlogan>{product.slogan[language] || product.slogan.en}</ProductSlogan>
                  )}
                  <ProductDescription>{productDescription}</ProductDescription>
                  {isAdmin && (
  <div style={{ marginTop: "8px" }}>
    <strong>Status:</strong>{" "}
    <span style={{ fontWeight: 600 }}>
      {product.availability?.[language] || (language === "fr" ? "Disponible" : "Available")}
    </span>
    <ButtonHolder style={{ marginTop: "4px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {product.availability?.en !== "Discontinued" && (
        <DiscontinueButton onClick={(e) => { e.preventDefault(); updateAvailability(product._id, "Discontinued"); }}>
          ❌ {language === "fr" ? "Discontinué" : "Discontinue"}
        </DiscontinueButton >
      )}
      {product.availability?.en !== "Out of Stock" && (
        <DiscontinueButton onClick={(e) => { e.preventDefault(); updateAvailability(product._id, "Out of Stock"); }}>
          🚫 {language === "fr" ? "Rupture de stock" : "Out of Stock"}
        </DiscontinueButton>
      )}
      {(product.availability?.en === "Out of Stock" || product.availability?.en === "Discontinued") && (
        <DiscontinueButton onClick={(e) => { e.preventDefault(); updateAvailability(product._id, "Available"); }}>
          ✅ {language === "fr" ? "Disponible" : "Available"}
        </DiscontinueButton>
      )}
    </ButtonHolder>
  </div>
)}
                  <Spacingdiv>
                  {!product?.type && (
                  <NutritionInfo>
                    <span>🥩 {product.protein} {language === "fr" ? "Protéines" : "Protein"}</span>
                    <span>🍭 {product.sugar} {language === "fr" ? "Sucres" : "Sugar"}</span>
                    <span>🔥 {product.calories} {language === "fr" ? "Calories" : "Calories"}</span>
                  </NutritionInfo>
                  )}
                  </Spacingdiv>
                  
                </ProductCard>
              </StyledLink>
            );
          })}
        </GridContainer>
        </>
        )}
      </ShopBody>
      <Footer />
    </div>
  );
};

export default Shop;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const RestrictedIcon = styled.img`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 60px;
  height: 60px;
  z-index: 2;
`;
const ProductSlogan = styled.div`
  font-style: italic;
  font-weight: 500;
  color: green;
  margin-bottom: 2px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgb(255, 255, 255);
  border-top: 4px solid rgb(241, 152, 129);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: 20px;
  font-size: 1.2rem;
  color: black;
  text-align: center;
`;

  const BtnSearchBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #ccc;
  font-size: 1rem;
`;

  const AvailabilityBubble = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${({ type }) => {
    if (type === "Discontinued") return "#e63946";
    if (type === "Out of Stock") return "#f4a261";
    return "#ccc";
  }};
  color: white;
  padding: 12px 30px;
  border-radius: 999px;
  font-size: 20px;
  font-style: italic;
  box-shadow: 0 4px 6px rgba(0,0,0,0.8);
  z-index: 2;
  text-align: center;
  white-space: nowrap;
`;

const ButtonGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1rem;
`;

const CategoryButton = styled.button`
  padding: 20px 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 17px;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  transition: background-color 0.3s, color 0.3s;
  background: ${({ selected }) =>
    selected ? 'rgba(148,163,135,1)' : 'rgba(255,255,255,0)'};
  color: ${({ selected }) => (selected ? 'white' : 'inherit')};
  border: none;

  &:hover {
    background-color: rgba(148,163,135,1);
    color: white;
  }

  &::before {
    content: "";
    position: absolute;
    margin-left: -4px;
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 1px;
    background-color: #ccc;
  }

  &:first-child::before {
    content: none;
  }
`;

  const ShopBody = styled.div`
    background:linear-gradient(122deg, rgba(148,163,135,1) 0%, rgba(186,204,171,1) 43%, rgba(246,187,155,1) 83%, rgba(217,121,97,1) 100%);
    padding: 20px;
    min-height: 100vh;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
  `;

const Toppagediv = styled.div`
  position: relative;
  width: 100%;
  padding-top: 20px;
`;

const Title = styled.p`
  color: white;
  font-weight: bold;
  font-size: clamp(32px, 5vw, 60px);
  text-align: center;
  margin: 0 auto;
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0  rgb(81, 131, 43);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const QcDisclaimerWrapper = styled.div`
  position: absolute;
  top: 100px;
  right: 20px;
  z-index: 10;
`;

const FullDisclaimer = styled.p`
  background-color: rgba(255, 255, 255, 0.7);
  padding: 5px 10px;
  border-radius: 5px;
  color: rgba(0, 0, 0, 0.5);
  max-width: 350px;
  font-size: 12px;

  @media (max-width: 1475px) {
    display: none;
  }
`;

const DisclaimerButton = styled.button`
  display: none;
    position: absolute;
  top: 5px;
  right: 0px;
  z-index: 10;

  @media (max-width: 1475px) {
    display: block;
    background: white;
    color: black;
    border: 2px solid  rgb(81, 131, 43);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    font-weight: bold;
    cursor: pointer;
    text-align: center;
    position: relative;
    animation: ${pulse} 2s infinite;
  }
`;

const PopupDisclaimer = styled.div`
  position: absolute;
  top: 30px;
  right: 25px;
  background-color: rgba(255,255,255,0.8);
  color: black;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  width: 250px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 20;
  animation: ${fadeIn} 0.3s ease-out;
`;

  const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const QcDisclaimer = styled.p`
  margin-top: 2rem;
  font-size: 0.85rem;
  color: black;
  font-weight: bold;
  background-color: rgba(255,255,255,0.5);
  max-width: 290px;
  padding: 10px 14px;
  border-radius: 10px;
`

const ProductCard = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 16px;
  height: 480px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  margin-bottom: 12px;
`;

const Image = styled.img`
  width: 220px;
  height: 190px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 12px;
`;

const ProductTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
`;

const ProductDescription = styled.p`
  font-size: 0.95rem;
  margin: 10px 0;
  color: #333;
  flex-grow: 1;
  max-height: 80px;
`;

const NutritionInfo = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-around;
  font-size: 0.9rem;
  padding: 8px;
  border-radius: 8px;
`;

const Spacingdiv = styled.div`
margin-top: 50px;
`


const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const FilterWrapper = styled.div`
  background: rgba(255,255,255,0.85);
  padding: 10px;
  margin-bottom: 20px;
  align-items: center;
  border-radius: 16px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
`;

const FilterGroup = styled.div`
  margin-bottom: 10px;
`

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
  margin-top: 10px;
`;


const ResetButton = styled.button`
  background-color: #e2725b;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 10px;
  font-weight: bold;

  &:hover {
    background-color: #c95c4d;
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const StyledCheckbox = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  background: ${props => props.checked ? '#4caf50' : '#fff'};
  border-radius: 60px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  border: 1px solid ${props => props.checked ? '#4caf50' : '#ccc'};
  cursor: pointer;
  overflow: hidden;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.35);
  }

  svg {
    stroke-dasharray: 22;
    stroke-dashoffset: ${props => props.checked ? '0' : '22'};
    transition: stroke-dashoffset 0.3s ease;
  }

  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(76, 175, 80, 0.3);
    border-radius: 50%;
    transform: scale(0);
    opacity: 0;
    animation: ${props => props.checked ? 'ripple 0.4s ease-out' : 'none'};
    pointer-events: none;
  }

  @keyframes ripple {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const Checkmark = styled.svg`
  width: 16px;
  height: 16px;
  stroke: white;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
`;

const DietaryHeader = styled.div`
  padding: 0.5rem 0;
  font-weight: bold;
`;

const ClickableHeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const Arrow = styled.span`
  display: inline-block;
  margin: 0px 5px;
  transition: transform 0.3s ease;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0deg)")};
`;

const DietaryFilterContainer = styled.div`
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.4s ease;
  max-height: ${({ open }) => (open ? "800px" : "0")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
`;

const DiscontinueButton = styled.button`
  background-color: #f4a261;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #e76f51;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const ButtonHolder = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  justify-content: center;
`;